import { compileOnlyAssertNever } from "@/utils/asserts";
import { getTailwindConfig } from "@/utils/color-util";

export interface MarketingColorPalette {
  logo: string;
  bg: string;
  title: string;
  subtitle: string;
  header: string;
  navText: string;
  navHighlight: string;
  textPrimary: string;
  textSecondary: string;
}

export type MarketingThemeMap = { [T in MarketingTheme]: MarketingColorPalette };

export enum MarketingTheme {
  LightGreen = "lightgreen",

  // only on the code-examples page
  Black = "black",
  MediumGreen = "mediumgreen",
  DarkGreen = "darkgreen",

  // used on solution pages
  Slate = "slate",
  LightGray = "lightgray",

  DarkGreenCMS = "darkgreen-cms",
  LightGrayCMS = "lightgray-cms",
  LightGreenCMS = "lightgreen-cms",
  MediumGreenCMS = "mediumgreen-cms",
  MintGreenCMS = "mintgreen-cms",
  PersianFableGreenCMS = "persian-fable-green-cms",
  SeafoamCMS = "seafoam-cms",
  NavyBlueCMS = "navyblue-cms",
  PurpleCMS = "purple-cms",
  BlackCMS = "black-cms",
  VioletLightCMS = "violet-light-cms",
}

export const colorsForTheme = (
  theme: MarketingTheme,
): MarketingColorPalette & { theme: MarketingTheme | undefined } => {
  const marketingTheme: MarketingThemeMap = (getTailwindConfig().theme.colors as any)["marketing"];
  switch (theme) {
    case MarketingTheme.Black:
    case MarketingTheme.DarkGreen:
    case MarketingTheme.DarkGreenCMS:
      return {
        theme,
        ...marketingTheme["darkgreen"],
      };
    case MarketingTheme.MediumGreen:
    case MarketingTheme.LightGreenCMS:
    case MarketingTheme.MediumGreenCMS:
      return {
        theme,
        ...marketingTheme["mediumgreen"],
      };
    case MarketingTheme.SeafoamCMS:
      return {
        theme,
        ...marketingTheme["seafoam-cms"],
      };
    case MarketingTheme.BlackCMS:
      return {
        theme,
        ...marketingTheme["darkgreen"],
        bg: "#000000",
        title: "#FFFFFF",
        subtitle: "#FFFFFF",
        header: "#FFFFFF",
        navText: "#FFFFFF",
        navHighlight: "#FFFFFF",
        textPrimary: "#FFFFFF",
        textSecondary: "#FFFFFF",
      };
    case MarketingTheme.NavyBlueCMS:
      return {
        theme,
        ...marketingTheme["navyblue-cms"],
      };
    case MarketingTheme.PurpleCMS:
      return {
        theme,
        ...marketingTheme["purple-cms"],
      };
    case MarketingTheme.MintGreenCMS:
      return {
        theme,
        ...marketingTheme["mintgreen-cms"],
      };
    case MarketingTheme.VioletLightCMS:
      return { theme, ...marketingTheme["violet-light-cms"] };
    case MarketingTheme.PersianFableGreenCMS:
      return {
        theme,
        ...marketingTheme["persian-fable-green-cms"],
      };
    case MarketingTheme.LightGray:
    case MarketingTheme.LightGrayCMS:
      return {
        theme,
        ...marketingTheme["lightgray"],
      };
    case MarketingTheme.LightGreen:
      return {
        theme,
        ...marketingTheme["lightgreen"],
      };
    case MarketingTheme.Slate:
      return {
        theme,
        ...marketingTheme["slate"],
      };
    default:
      compileOnlyAssertNever(theme);
      return {
        theme,
        ...marketingTheme["darkgreen"],
      };
  }
};

/**
 * If we set the color of some text via the style: {color: ...} prop, then that color
 * overrides the tailwind `hover:...` class, so we need to get link hover colors as classnames
 */
export const linkColorClassesForTheme = (theme: MarketingTheme | undefined): string => {
  switch (theme) {
    case MarketingTheme.Black:
    case MarketingTheme.DarkGreen:
      return "text-marketing-darkgreen-textPrimary hover:text-white";
    case MarketingTheme.DarkGreenCMS:
      return "text-white hover:text-marketing-mint-green";
    case MarketingTheme.BlackCMS:
      return "text-white hover:opacity-70";
    case MarketingTheme.PurpleCMS:
      return "text-white hover:opacity-70";
    case MarketingTheme.MediumGreen:
      return "text-marketing-mediumgreen-textPrimary hover:text-white";
    case MarketingTheme.PersianFableGreenCMS:
      return "text-marketing-fablegreen-textPrimary hover:opacity-70";

    case MarketingTheme.LightGreen:
      return "text-marketing-lightgreen-textPrimary hover:text-white";
    case MarketingTheme.LightGray:
      return "text-marketing-lightgreen-textPrimary hover:text-marketing-lightgreen-textSecondary";
    case MarketingTheme.Slate:
      return "text-marketing-slate-textPrimary hover:text-white";
    case undefined:
      return "text-marketing-darkgreen-textPrimary hover:text-white";
    default:
      return "text-marketing-darkgreen-textPrimary hover:text-white";
  }
};

const THEME_BG_MAP = {
  [MarketingTheme.Black]: "bg-black",
  [MarketingTheme.DarkGreen]:
    // "bg-gradient-to-b from-marketing-darkgreen-bg via-marketing-darkgreen-bg to-marketing-darkgreen-bg",
    // "darkgreen-gradient",
    "bg-marketing-darkgreen-bg",
  [MarketingTheme.DarkGreenCMS]:
    // "bg-gradient-to-b from-marketing-darkgreen-bg via-marketing-darkgreen-bg to-marketing-darkgreen-bg",
    // "darkgreen-gradient",
    "bg-marketing-darkgreen-bg",
  [MarketingTheme.MediumGreen]:
    // "bg-gradient-to-b from-marketing-mediumgreen-bg via-marketing-mediumgreen-bg to-marketing-darkgreen-bg",
    // "mediumgreen-gradient",
    "bg-marketing-mediumgreen-bg",
  [MarketingTheme.MediumGreenCMS]:
    // "bg-gradient-to-b from-marketing-mediumgreen-bg via-marketing-mediumgreen-bg to-marketing-darkgreen-bg",
    // "mediumgreen-gradient",
    "bg-marketing-green-19",
  [MarketingTheme.LightGreenCMS]:
    // "bg-gradient-to-b from-marketing-mediumgreen-bg via-marketing-mediumgreen-bg to-marketing-darkgreen-bg",
    // "mediumgreen-gradient",
    "bg-marketing-mediumgreen-bg",
  [MarketingTheme.LightGray]:
    // "bg-gradient-to-b from-marketing-lightgreen-bg via-marketing-lightgreen-bg to-marketing-darkgreen-bg",
    // "lightgreen-gradient",
    "bg-marketing-lightgray-bg",
  [MarketingTheme.LightGrayCMS]:
    // "bg-gradient-to-b from-marketing-lightgreen-bg via-marketing-lightgreen-bg to-marketing-darkgreen-bg",
    // "lightgreen-gradient",
    "bg-marketing-lightgray-bg",
  [MarketingTheme.LightGreen]:
    // "bg-gradient-to-b from-marketing-lightgreen-bg via-marketing-lightgreen-bg to-marketing-darkgreen-bg",
    // "lightgreen-gradient",
    "bg-marketing-lightgreen-bg",
  [MarketingTheme.Slate]:
    // "bg-gradient-to-b from-marketing-slate-bg via-marketing-slate-bg to-marketing-darkgreen-bg",
    // "slate-gradient",
    "bg-marketing-slate-bg",
  [MarketingTheme.MintGreenCMS]:
    // "bg-gradient-to-b from-marketing-slate-bg via-marketing-slate-bg to-marketing-darkgreen-bg",
    // "slate-gradient",
    "bg-marketing-mint-green",
  [MarketingTheme.PersianFableGreenCMS]: "bg-marketing-persian-fable-green",
  [MarketingTheme.SeafoamCMS]:
    // "bg-gradient-to-b from-marketing-slate-bg via-marketing-slate-bg to-marketing-darkgreen-bg",
    // "slate-gradient",
    "bg-marketing-accent-seafoam",
  [MarketingTheme.NavyBlueCMS]: "bg-marketing-navy-blue",
  [MarketingTheme.PurpleCMS]: "bg-marketing-violet",
  [MarketingTheme.BlackCMS]: "bg-black",
  [MarketingTheme.VioletLightCMS]: "bg-white",
} satisfies Record<MarketingTheme, string>;

export const MarketingLightThemes = new Set<MarketingTheme | null | undefined>([
  MarketingTheme.MediumGreenCMS,
  MarketingTheme.MintGreenCMS,
  MarketingTheme.PersianFableGreenCMS,
  MarketingTheme.LightGreen,
  MarketingTheme.SeafoamCMS,
  MarketingTheme.VioletLightCMS,
]);

const DEFAULT_THEME_BG = THEME_BG_MAP[MarketingTheme.DarkGreen];

export const allThemeBGs = [
  THEME_BG_MAP[MarketingTheme.Black],
  THEME_BG_MAP[MarketingTheme.DarkGreen],
  THEME_BG_MAP[MarketingTheme.MediumGreen],
  THEME_BG_MAP[MarketingTheme.LightGreen],
  THEME_BG_MAP[MarketingTheme.Slate],
  "bg-white",
];

export const bgForTheme = (theme: MarketingTheme | undefined): string => {
  if (theme == null || !(theme in THEME_BG_MAP)) {
    return DEFAULT_THEME_BG;
  } else {
    return THEME_BG_MAP[theme];
  }
};

export interface MarketingMultiColors {
  lightgreen: string;
  yellow: string;
  orange: string;
  purple: string;
  blue: string;
}

export const marketingMultiColors = (): MarketingMultiColors =>
  (getTailwindConfig().theme.colors as any)["marketing"]["colors"];
